
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Prospect } from '@/types/pipeline';
import { Phone, Mail, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import GlassPill from '@/components/ui/GlassPill';

interface PipelineCardProps {
  prospect: Prospect;
  onClick: (prospect: Prospect) => void;
  columnId?: string;
}

const PipelineCard = ({ prospect, onClick, columnId }: PipelineCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: prospect.id,
    data: {
      type: 'prospect',
      prospect,
      columnId
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing bg-white/50 dark:bg-slate-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 p-4 mb-3 shadow-sm transition-all duration-200 select-none"
      onClick={() => onClick(prospect)}
      data-prospect-id={prospect.id}
      data-column-id={columnId}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-gray-500" />
            <h3 className="font-medium text-lg">
              {prospect.name}
            </h3>
          </div>
        </div>

        {prospect.phone && (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Phone size={14} className="flex-shrink-0" />
            <p className="truncate">{prospect.phone}</p>
          </div>
        )}

        {prospect.email && (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Mail size={14} className="flex-shrink-0" />
            <p className="truncate">{prospect.email}</p>
          </div>
        )}

        <div className="flex items-center justify-between mt-2">
          {prospect.industry && (
            <div className="flex items-center">
              <GlassPill 
                type={prospect.industry}
                variant="blue"
                className="text-xs"
              />
            </div>
          )}
          {prospect.value > 0 && (
            <div className="bg-accent/20 text-accent rounded-full px-2 py-0.5 text-xs font-medium ml-auto">
              ${prospect.value}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PipelineCard;
